import { useState } from 'react';
import paymentSchemesApis from '../../../apis/payments/paymentSchemes';
import { SnackbarSeverityType } from '../../../enums';
import {
  PaymentOption,
  PaymentSchemeHookProps,
  PaymentSchemeMasterProps,
  PaymentSchemeUpdatePayload,
  UpdatePaymentSchemeDetail
} from '../types';
import { isEmpty } from 'lodash';

export const usePaymentSchemeApiHook = ({
  admissionType,
  school,
  academicYear,
  grade,
  showToast
}: PaymentSchemeHookProps) => {
  const [remarksUpdating, setRemarksUpdating] = useState<boolean>(false);
  const [paymentSchemeData, setPaymentSchemeData] =
    useState<PaymentSchemeMasterProps | null>();

  const paymentOptionData =
    admissionType === 'newAdmission'
      ? paymentSchemeData?.newAdmissionPaymentOptions
      : paymentSchemeData?.paymentOptions;

  const handlePaymentSchemeUpdate = async (
    payload: PaymentSchemeUpdatePayload
  ) => {
    setRemarksUpdating(true);
    try {
      const response = await paymentSchemesApis.updatePaymentScheme({
        school,
        academicYear,
        grade,
        payload: payload?.data
      });

      setPaymentSchemeData(response as any);
      showToast(payload?.successMessage, SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || payload?.errorMessage,
        SnackbarSeverityType.error
      );
    } finally {
      setRemarksUpdating(false);
    }
  };

  const paymentSchemeRemarksUpdate = ({ newRemark }: { newRemark: string }) => {
    const payload = {
      remarks: {
        rollOver:
          admissionType === 'rollover'
            ? newRemark
            : paymentSchemeData?.remarks?.rollOver || '',
        newAdmission:
          admissionType === 'newAdmission'
            ? newRemark
            : paymentSchemeData?.remarks?.newAdmission || ''
      },
      component: 'Academic Fees'
    };
    const successMessage = 'Remarks updated successfully';
    const errorMessage = 'Error updating remarks';
    handlePaymentSchemeUpdate({ data: payload, successMessage, errorMessage });
  };

  const handlePaymentSchemeDelete = ({
    selectedRowData
  }: {
    selectedRowData: PaymentOption | null;
  }) => {
    const existingPaymentSchemeData = paymentOptionData || [];

    const updatedData =
      existingPaymentSchemeData.filter(
        (paymentOption) => paymentOption._id !== selectedRowData?._id
      ) || [];

    const payload = {
      paymentOptions: admissionType === 'rollover' ? updatedData : undefined,
      newAdmissionPaymentOptions:
        admissionType === 'rollover' ? undefined : updatedData,
      component: 'Academic Fees'
    };

    const successMessage = 'Successfully deleted payment scheme option';
    const errorMessage = 'Error deleting payment scheme option';
    handlePaymentSchemeUpdate({ data: payload, successMessage, errorMessage });
  };

  const handleAddPaymentScheme = ({
    selectedPaymentSchemeName
  }: {
    selectedPaymentSchemeName: string;
  }) => {
    const existingPaymentSchemeData = paymentOptionData || [];

    const updatedData = [
      ...(!isEmpty(existingPaymentSchemeData) ? existingPaymentSchemeData : []),
      {
        schemeName: selectedPaymentSchemeName.trim(),
        installments: [],
        attachmentUrls: [],
        formFAttachments: [],
        schemeAttachmentUrls: []
      }
    ];

    const payload = {
      paymentOptions: admissionType === 'rollover' ? updatedData : undefined,
      newAdmissionPaymentOptions:
        admissionType === 'rollover' ? undefined : updatedData,
      component: 'Academic Fees'
    };
    const successMessage = 'Successfully added payment scheme';
    const errorMessage = 'Error adding payment scheme';
    handlePaymentSchemeUpdate({ data: payload, successMessage, errorMessage });
  };

  const handlePaymentSchemeDetailUpdate = ({
    newFormFList,
    newInstallmentsData,
    selectedPaymentSchemeId
  }: UpdatePaymentSchemeDetail) => {
    const existingPaymentSchemeData = paymentOptionData || [];
    const formFAttachments =
      newFormFList.map((formF) => ({
        filename: formF.ev_filename || formF.filename,
        url: formF.ev_url || formF.url,
        mimetype: formF.ev_mimetype || formF.mimetype,
        size: formF.ev_size || formF.size
      })) || [];

    const updatedData = existingPaymentSchemeData.map((paymentOption) => {
      if (paymentOption._id === selectedPaymentSchemeId) {
        return {
          ...paymentOption,
          installments: newInstallmentsData,
          formFAttachments
        };
      }
      return paymentOption;
    });

    const payload = {
      paymentOptions: admissionType === 'rollover' ? updatedData : undefined,
      newAdmissionPaymentOptions:
        admissionType === 'rollover' ? undefined : updatedData,
      component: 'Academic Fees'
    };
    const successMessage = 'Successfully updated payment scheme';
    const errorMessage = 'Error updating payment scheme';
    handlePaymentSchemeUpdate({ data: payload, successMessage, errorMessage });
  };

  const handlePaymentSchemeClone = async ({
    academicYearToCloneFrom,
    gradeToCloneFrom
  }: {
    academicYearToCloneFrom: string;
    gradeToCloneFrom: string;
  }) => {
    const payload = {
      isNewAdmissionPaymentOption: admissionType === 'newAdmission',
      cloneFrom: {
        academicYear: academicYearToCloneFrom,
        grade: gradeToCloneFrom
      }
    };
    try {
      const response = await paymentSchemesApis.clonePaymentScheme({
        school: school,
        academicYear,
        grade,
        payload
      });
      setPaymentSchemeData(response as any);
      showToast(
        'Payment Scheme Cloned successfully',
        SnackbarSeverityType.success
      );
    } catch (error: any) {
      showToast(
        error.error?.[0].message || 'Error cloning paymentScheme',
        SnackbarSeverityType.error
      );
    }
  };

  return {
    remarksUpdating,
    paymentSchemeData,
    paymentSchemeRemarksUpdate,
    setPaymentSchemeData,
    handlePaymentSchemeDelete,
    handleAddPaymentScheme,
    handlePaymentSchemeClone,
    handlePaymentSchemeDetailUpdate
  };
};
